import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import styles from './csv-import-setup-body.css?inline';

import {
  DEFAULT_ACCEPTED_FILE_TYPE,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_MAX_FILE_SIZE_LABEL,
} from '@inmar/component-lib';

import type { SetupView } from '../../types/views';

// ============================================================================
// Types
// ============================================================================

export interface RequiredField {
  name: string;
  description?: string;
}

export interface CsvImportSetupBodyDownloadTemplateEvent {
  element: CsvImportSetupBody;
}

export interface CsvImportSetupBodyFileStateEvent {
  file: File | null;
  error: string | null;
  element: CsvImportSetupBody;
}

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementEventMap {
    'csv-import-download-template': CustomEvent<CsvImportSetupBodyDownloadTemplateEvent>;
    'csv-import-file-state': CustomEvent<CsvImportSetupBodyFileStateEvent>;
  }
  interface HTMLElementTagNameMap {
    'csv-import-setup-body': CsvImportSetupBody;
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Body content for the "Select File" step of the CSV Import flow.
 *
 * This is a controlled component — it owns no workflow state. The orchestrator
 * passes the resolved `view` prop and listens for `csv-import-file-state`
 * events to update its own state.
 *
 * @fires csv-import-file-state - Fired on every file-picker change.
 * @fires csv-import-next - Fired when the user clicks Next (no file payload).
 * @fires csv-import-close - Fired when the user clicks Cancel.
 * @fires csv-import-download-template - Fired when the user clicks "Download template".
 */
@customElement('csv-import-setup-body')
export class CsvImportSetupBody extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  // ============================================================================
  // Properties (all controlled by orchestrator)
  // ============================================================================

  /** The fully-resolved view state, computed by the orchestrator. */
  @property({ type: String })
  view: SetupView = 'idle';

  /** Display name of the committed file (null when no file selected). */
  @property({ type: String, attribute: 'selected-file-name' })
  selectedFileName: string | null = null;

  /** Local file-validation error message (null when no error). */
  @property({ type: String, attribute: 'file-error-message' })
  fileErrorMessage: string | null = null;

  /** List of required field definitions shown in the collapsible section. */
  @property({ type: Array, attribute: false })
  requiredFields: RequiredField[] = [];

  /** Maximum file size in bytes used for upload validation. */
  @property({ type: Number, attribute: 'max-file-size' })
  maxFileSize = DEFAULT_MAX_FILE_SIZE;

  /** Human-readable maximum file size label (e.g. "(max 50MB)"). */
  @property({ type: String, attribute: 'max-file-size-label' })
  maxFileSizeLabel = DEFAULT_MAX_FILE_SIZE_LABEL;

  // ============================================================================
  // Internal state (UI-affordance only)
  // ============================================================================

  @state() private _requiredFieldsOpen = false;

  // ============================================================================
  // Event handlers
  // ============================================================================

  private _onFileSelected(e: CustomEvent<{ file: File }>) {
    this.dispatchEvent(
      new CustomEvent<CsvImportSetupBodyFileStateEvent>(
        'csv-import-file-state',
        {
          bubbles: true,
          composed: true,
          detail: { file: e.detail.file, error: null, element: this },
        },
      ),
    );
  }

  private _onFileRemoved() {
    this.dispatchEvent(
      new CustomEvent<CsvImportSetupBodyFileStateEvent>(
        'csv-import-file-state',
        {
          bubbles: true,
          composed: true,
          detail: { file: null, error: null, element: this },
        },
      ),
    );
  }

  private _onFileError(_e: CustomEvent<{ reason: string; message?: string }>) {
    // Always use a consistent user-facing message for format errors (invalid type/size).
    // "CSV" is hardcoded — the template doesn't specify file type, and this is a CSV-only importer.
    // maxFileSize comes from the template config (always a round MB value in practice).
    const sizeInMB = Math.floor(this.maxFileSize / (1024 * 1024));
    const errorMessage = `Your file must be a CSV with a maximum size of ${sizeInMB}MB`;

    this.dispatchEvent(
      new CustomEvent<CsvImportSetupBodyFileStateEvent>(
        'csv-import-file-state',
        {
          bubbles: true,
          composed: true,
          detail: {
            file: null,
            error: errorMessage,
            element: this,
          },
        },
      ),
    );
  }

  private _onDownloadTemplate() {
    this.dispatchEvent(
      new CustomEvent<CsvImportSetupBodyDownloadTemplateEvent>(
        'csv-import-download-template',
        {
          bubbles: true,
          composed: true,
          detail: { element: this },
        },
      ),
    );
  }

  private _toggleRequiredFields() {
    this._requiredFieldsOpen = !this._requiredFieldsOpen;
  }

  /**
   * Stops synthetic (programmatic) clicks — e.g. input.click() from the file
   * picker — from bubbling up to the modal's backdrop-click handler, which
   * would otherwise close the modal because the coordinates are (0,0).
   */
  private _stopSyntheticClick(e: MouseEvent) {
    if (!e.isTrusted) e.stopPropagation();
  }

  // ============================================================================
  // Render
  // ============================================================================

  override render() {
    return html`
      <div @click=${this._stopSyntheticClick}>${this._renderBody()}</div>
    `;
  }

  private _renderBody() {
    switch (this.view) {
      case 'template-download-error':
        return this._renderTemplateDownloadError();
      case 'file-format-error':
        return this._renderFileFormatError();
      case 'file-upload-error':
        return this._renderFileUploadError();
      case 'processing':
        return this._renderProcessing();
      case 'preview-error':
        return this._renderPreviewError();
      case 'file-selected':
        return this._renderFileSelected();
      case 'idle':
      default:
        return this._renderIdle();
    }
  }

  private _renderIdle() {
    return html`
      <div
        class=${classMap({
          body: true,
          'body--fields-open': this._requiredFieldsOpen,
        })}
      >
        ${this._renderIntro()}
        <div class="setup-grid">
          <div class="left-column">${this._renderUploadArea()}</div>
          <div class="right-column">
            ${this._renderDownloadTemplate()} ${this._renderRequiredFields()}
          </div>
        </div>
      </div>
    `;
  }

  private _renderFileSelected() {
    return html`
      <div
        class=${classMap({
          body: true,
          'body--fields-open': this._requiredFieldsOpen,
        })}
      >
        ${this._renderIntro()}
        <div class="setup-grid">
          <div class="left-column">
            <div class="file-selected-indicator">
              <span class="file-selected-icon">
                <hcds-icon
                  name="paperclip-vertical"
                  family="classic"
                  icon-style="light"
                ></hcds-icon>
              </span>
              <span class="file-selected-name">${this.selectedFileName}</span>
              <inmar-hc-button
                variant="tertiary"
                size="xs"
                icon-only
                button-aria-label="Remove file ${this.selectedFileName}"
                @button-click=${this._onFileRemoved}
              >
                <hcds-icon
                  slot="icon"
                  name="xmark"
                  family="classic"
                  icon-style="light"
                ></hcds-icon>
              </inmar-hc-button>
            </div>
          </div>
          <div class="right-column">
            ${this._renderDownloadTemplate()} ${this._renderRequiredFields()}
          </div>
        </div>
      </div>
    `;
  }

  private _renderFileUploadError() {
    return html`
      <div class="body body--error">
        <inmar-hc-alert
          type="error"
          heading-text="We were unable to upload your file."
          body-text="Please try again or submit a help ticket."
        ></inmar-hc-alert>
        ${this._renderIntro()}
        <div class="setup-grid">
          <div class="left-column">${this._renderUploadArea()}</div>
          <div class="right-column">
            ${this._renderDownloadTemplate()} ${this._renderRequiredFields()}
          </div>
        </div>
      </div>
    `;
  }

  private _renderFileFormatError() {
    return html`
      <div class="body body--error">
        <inmar-hc-alert
          type="error"
          heading-text="Please upload a different file"
        ></inmar-hc-alert>
        ${this._renderIntro()}
        <div class="setup-grid">
          <div class="left-column">${this._renderUploadAreaError()}</div>
          <div class="right-column">
            ${this._renderDownloadTemplate()} ${this._renderRequiredFields()}
          </div>
        </div>
      </div>
    `;
  }

  private _renderProcessing() {
    return html`
      <div class="body">
        <div class="processing-indicator">
          <inmar-hc-spinner
            size="large"
            variant="dark"
            aria-label="Processing your file"
          ></inmar-hc-spinner>
        </div>
      </div>
    `;
  }

  private _renderPreviewError() {
    return html`
      <div class="body body--error">
        <inmar-hc-alert
          type="error"
          heading-text="We were unable to preview your file."
          body-text="Please try again or submit a help ticket."
        ></inmar-hc-alert>
        ${this._renderIntro()}
        <div class="setup-grid">
          <div class="left-column">
            <div class="file-selected-indicator">
              <span class="file-selected-icon">
                <hcds-icon
                  name="paperclip-vertical"
                  family="classic"
                  icon-style="light"
                ></hcds-icon>
              </span>
              <span class="file-selected-name">${this.selectedFileName}</span>
              <inmar-hc-button
                variant="tertiary"
                size="xs"
                icon-only
                button-aria-label="Remove file ${this.selectedFileName}"
                @button-click=${this._onFileRemoved}
              >
                <hcds-icon
                  slot="icon"
                  name="xmark"
                  family="classic"
                  icon-style="light"
                ></hcds-icon>
              </inmar-hc-button>
            </div>
          </div>
          <div class="right-column">
            ${this._renderDownloadTemplate()} ${this._renderRequiredFields()}
          </div>
        </div>
      </div>
    `;
  }

  private _renderTemplateDownloadError() {
    return html`
      <div class="body body--error">
        <inmar-hc-alert
          type="error"
          heading-text="The template could not be downloaded."
          body-text="Please try again or submit a help ticket."
        ></inmar-hc-alert>
        ${this._renderIntro()}
        <div class="setup-grid">
          <div class="left-column">${this._renderUploadArea()}</div>
          <div class="right-column">
            ${this._renderDownloadTemplate()} ${this._renderRequiredFields()}
          </div>
        </div>
      </div>
    `;
  }

  // --------------------------------------------------------------------------
  // Shared render helpers
  // --------------------------------------------------------------------------

  private _renderIntro() {
    return html`
      <div class="intro">
        <p class="section-title">Select File</p>
        <p class="section-subtitle">
          Choose a CSV file to upload. You can use our template, or upload your
          own file as long as it contains the required fields.
        </p>
      </div>
    `;
  }

  private _renderUploadArea() {
    return html`
      <inmar-hc-upload-area-drag
        .accept=${DEFAULT_ACCEPTED_FILE_TYPE}
        .maxFileSize=${this.maxFileSize}
        .maxFileSizeLabel=${this.maxFileSizeLabel}
        .fileTypeText=${`${DEFAULT_ACCEPTED_FILE_TYPE} files only`}
        .errorMessage=${this.fileErrorMessage ?? ''}
        aria-label="Upload CSV file"
        @file-selected=${this._onFileSelected}
        @file-removed=${this._onFileRemoved}
        @file-error=${this._onFileError}
      ></inmar-hc-upload-area-drag>
    `;
  }

  private _renderUploadAreaError() {
    return html`
      <inmar-hc-upload-area-drag
        state="error"
        error-message=${this.fileErrorMessage ?? ''}
        .accept=${DEFAULT_ACCEPTED_FILE_TYPE}
        .maxFileSize=${this.maxFileSize}
        .maxFileSizeLabel=${this.maxFileSizeLabel}
        .fileTypeText=${`${DEFAULT_ACCEPTED_FILE_TYPE} files only`}
        aria-label="Upload CSV file"
        @file-selected=${this._onFileSelected}
        @file-removed=${this._onFileRemoved}
        @file-error=${this._onFileError}
      ></inmar-hc-upload-area-drag>
    `;
  }

  private _renderDownloadTemplate() {
    return html`
      <div class="download-btn-wrapper">
        <inmar-hc-button
          label="Download template"
          variant="secondary"
          @button-click=${this._onDownloadTemplate}
        >
          <hcds-icon
            slot="left-icon"
            name="download"
            family="classic"
            icon-style="light"
          ></hcds-icon>
        </inmar-hc-button>
      </div>
    `;
  }

  private _renderRequiredFields() {
    return html`
      <div class="required-fields">
        <button
          class="required-fields-header"
          type="button"
          aria-expanded=${this._requiredFieldsOpen}
          aria-controls="required-fields-list"
          @click=${this._toggleRequiredFields}
        >
          Required fields
          <span
            class=${classMap({
              chevron: true,
              open: this._requiredFieldsOpen,
            })}
            aria-hidden="true"
          >
            <hcds-icon
              name="chevron-down"
              family="classic"
              icon-style="light"
            ></hcds-icon>
          </span>
        </button>
        <div
          id="required-fields-list"
          class=${classMap({
            'required-fields-body': true,
            open: this._requiredFieldsOpen,
          })}
        >
          ${this.requiredFields.map(
            (field) => html`
              <div class="required-fields-field">
                <p class="required-fields-field-name">${field.name}</p>
                ${field.description
                  ? html`<p class="required-fields-field-description">
                      ${field.description}
                    </p>`
                  : nothing}
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }
}
